export enum ViewMode {
  NORMAL = 'normal',
  FULLSCREEN = 'fullscreen',
}

/**
 * Value object of a container with the current bounding rect and if the element is displayed in fullscreen or not.
 */
export interface ElementSize {
  boundingRect: DOMRect;
  viewMode: ViewMode.NORMAL | ViewMode.FULLSCREEN;
}

/**
 * A simple player abstraction to load and playback progressive media and modify the ad playback.
 *
 * @remarks
 * Ad playback is not encrypted and is not concerned with multiple tracks, qualities and languages,
 * thus we have a different API in contrast to the PlayerAdapter interface.
 *
 * An additional need for the AdPlayer is to trigger the Lock- and Release-Content procedures to switch seamlessly between ads and content.
 *
 * @privateRemarks
 * The Ad-Player is a dependency of the advertising adapter and also of the Smartclip SDK.
 * We only communicate with the Ad-Player during ad playback via methods like play, pause, mute and so on.
 * Smartclip triggers the construction and tear down methods like lockContent, releaseContent, load, addEventListener and so on.
 *
 * TODO https://rtltech.atlassian.net/browse/FOUN-2698: Let AdPlayer implement Facade interface/type from Smartclip SDK 7.0
 *
 * @internal
 */
export interface AdPlayer {
  /**
   * Load a single progressive clip.
   *
   * @param url Source of the clip
   */
  load(url: string): Promise<void>;

  /**
   * Register a single event listener for a specified event.
   *
   * @param type The type of event you want to listen on
   * @param callback The callback to fire
   */
  addEventListener(type: string, callback: () => void): void;

  /**
   * Remove a registered event listener for a specified event.
   *
   * @param type The type of event you want to remove
   * @param callback The callback to remove
   */
  removeEventListener(type: string, callback: () => void): void;

  /**
   * Pass a handler to pass all relevant ad player events back into the foundation player.
   *
   * @param handler A listener a process the ad player events
   */
  onEvent(handler: (event: Event) => void): void;

  /**
   * Save the current playback state and prepare for ad playback.
   */
  lockContent(): Promise<void>;

  /**
   * Stop ad playback and return to the last saves playback state.
   */
  releaseContent(): Promise<void>;

  /**
   * Return the source of the currently loaded clip.
   */
  getCurrentContentSource(): string | undefined;

  /**
   * Return the playhead of the current clip.
   */
  getCurrentTime(): number;

  /**
   * Return the duration of the current clip.
   */
  getDuration(): number;

  /**
   * Return the ElementSize of the ad player.
   */
  getPlayerSize(): ElementSize;

  /**
   * Return the ElementSize of the viewport.
   */
  getViewportSize(): ElementSize;

  /**
   * Return the native video element the clip is being played in.
   *
   * @deprecated Use getPlayerElement instead
   */
  getVideoElement(): HTMLVideoElement | undefined;

  /**
   * Return the native video element the clip is being played in.
   * NOTE: To enable SIMID ads your Ad-Player MUST implement this method!
   */
  getPlayerElement(): HTMLVideoElement | undefined;

  /**
   * Set the ad playback volume on a scale of 0 to 1
   *
   * @param volume Define volume on a scale of 0 to 1
   */
  setVolume(volume: number): Promise<void>;

  /**
   * Set the ad playback volume on a scale of 0 to 1
   */
  getVolume(): number;

  /**
   * Check if ad playback is muted
   */
  isMuted(): boolean;

  /**
   * Mute ad playback
   */
  mute(): Promise<void>;

  /**
   * Unmute ad playback
   */
  unmute(): Promise<void>;

  /**
   * Check if ad playback is paused
   */
  isPaused(): boolean;

  /**
   * Pause ad playback
   */
  pause(): Promise<void>;

  /**
   * Continue ad playback
   */
  play(): Promise<void>;

  /**
   * Seek to a specified position in the clip.
   */
  seek(offset: number): Promise<void>;

  /**
   * Destroy the ad player and free up all resources.
   * You must not use this ad player again after destroying. You will have to create a new ad player instead!
   */
  destroy(): void;
}
